import type React from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Footer from '../../components/Footer/Footer';
import Header from '../../components/Header/Header';
import { sendContactMessage } from '../../services/contactService';
import './Contact.css';

const Contact: React.FC = () => {
  const { t } = useTranslation();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');

  // Basic validation: Name handles optionally empty, but email and message are fundamentally required
  // Basic validation: Name, email and message are required
  const isFormValid = name.trim() !== '' && email.trim() !== '' && message.trim() !== '';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isFormValid || status === 'sending') return;

    setStatus('sending');
    const success = await sendContactMessage(name, email, message);

    if (success) {
      setStatus('success');
      // Reset form variables
      setName('');
      setEmail('');
      setMessage('');

      // Reset status after a few seconds so they can see the form again if needed
      setTimeout(() => {
        setStatus('idle');
      }, 5000);
    } else {
      setStatus('error');
      // allow user to retry by turning back to idle
      setTimeout(() => setStatus('idle'), 3000);
    }
  };

  return (
    <div className="contact-page">
      <div className="bg-accent-shape" />

      <Header />

      <main className="contact-main">
        <div className="contact-container">
          <section className="contact-left">
            <div className="contact-header-small">
              <div className="hero-line" />
              <h2 className="hero-label">{t('contact.title', 'Contact us')}</h2>
            </div>
            <h1 className="hero-title">{t('contact.formTitle', 'Reach out to our team')}</h1>

            <h3 className="contact-subtitle">
              {t('contact.subtitle', "We'd Love to Hear From You")}
            </h3>
            <p className="contact-description">
              {t(
                'contact.description',
                "Contact us for inquiries, support, or feedback. We're here to assist you every step of the way.",
              )}
            </p>

            <div className="contact-info-list">
              <div className="info-item">
                <div className="info-icon">
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                  >
                    <title>Location</title>
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                    <circle cx="12" cy="10" r="3" />
                  </svg>
                </div>
                <div className="info-content">
                  <h4>{t('contact.addressLabel', 'Address')}</h4>
                  <p>
                    THE AGE OF LIGHT INC
                    <br />
                    400 SPECTRUM CENTER DR.
                    <br />
                    SUITE 1900 IRVINE, CA
                  </p>
                </div>
              </div>

              <div className="info-item">
                <div className="info-icon">
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.5"
                  >
                    <title>Email</title>
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                    <polyline points="22,6 12,13 2,6" />
                  </svg>
                </div>
                <div className="info-content">
                  <h4>{t('contact.emailSupportLabel', 'Email Support')}</h4>
                  {(() => {
                    const supportEmail = t('contact.emailSupportAddress', 'support@theychat.ai');
                    return <a href={`mailto:${supportEmail}`}>{supportEmail}</a>;
                  })()}
                </div>
              </div>
            </div>
          </section>

          <section className="contact-right">
            <div className="contact-form-card" style={{ position: 'relative' }}>
              {/* Overlay states for success / error */}
              <div
                className={`form-overlay ${status === 'success' || status === 'error' ? 'active' : ''}`}
                style={{
                  position: 'absolute',
                  inset: 0,
                  display: status === 'success' || status === 'error' ? 'flex' : 'none',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background:
                    'radial-gradient(133.22% 133.16% at 42.65% 31.82%, #ffffff 50%, #decaae 100%)',
                  borderRadius: '16px',
                  zIndex: 10,
                  opacity: status === 'success' || status === 'error' ? 1 : 0,
                  transition: 'opacity 0.3s ease',
                  padding: '32px',
                  textAlign: 'center',
                  border: status === 'error' ? '1px solid var(--error-color, #ff4d4f)' : 'none',
                }}
              >
                {status === 'success' ? (
                  <>
                    <svg
                      width="64"
                      height="64"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="var(--primary-color)"
                      strokeWidth="1.5"
                      style={{ marginBottom: '16px' }}
                    >
                      <title>Success</title>
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                      <polyline points="22 4 12 14.01 9 11.01"></polyline>
                    </svg>
                    <h3
                      style={{
                        fontSize: '1.5rem',
                        marginBottom: '8px',
                        color: 'var(--text-primary)',
                      }}
                    >
                      {t('contact.successTitle', 'Message Sent')}
                    </h3>
                    <p style={{ color: 'var(--text-secondary)' }}>
                      {t(
                        'contact.successMessage',
                        'Thank you! We have received your message and will get back to you shortly.',
                      )}
                    </p>
                  </>
                ) : (
                  <>
                    <div
                      style={{
                        width: '64px',
                        height: '64px',
                        borderRadius: '50%',
                        border: '2px solid var(--error-color, #ff4d4f)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginBottom: '16px',
                        color: 'var(--error-color, #ff4d4f)',
                      }}
                    >
                      <svg
                        width="32"
                        height="32"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <title>Failed</title>
                        <line x1="18" y1="6" x2="6" y2="18"></line>
                        <line x1="6" y1="6" x2="18" y2="18"></line>
                      </svg>
                    </div>
                    <h3
                      style={{
                        fontSize: '1.5rem',
                        marginBottom: '8px',
                        color: 'var(--error-color, #ff4d4f)',
                        fontWeight: '700',
                      }}
                    >
                      {t('contact.errorTitle', 'Failed to send')}
                    </h3>
                    <p style={{ color: 'var(--text-secondary)', fontSize: '1.1rem' }}>
                      {t(
                        'contact.errorMessage',
                        'Please check your internet connection and try again.',
                      )}
                    </p>
                  </>
                )}
              </div>

              <div className="form-header-badge-row">
                <div className="form-header-line" />
                <span className="form-badge-text">
                  {t('contact.formTitle', 'Reach out to our team')}
                </span>
              </div>

              <form className="contact-form" onSubmit={handleSubmit}>
                <div className="form-group">
                  <label htmlFor="name">{t('contact.nameLabel', 'Name *')}</label>
                  <input
                    type="text"
                    id="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    onInvalid={(e) => {
                      const target = e.target as HTMLInputElement;
                      if (target.validity.valueMissing) {
                        target.setCustomValidity(t('validation.nameRequired', 'Name is required'));
                      }
                    }}
                    onInput={(e) => (e.target as HTMLInputElement).setCustomValidity('')}
                    placeholder={t('contact.namePlaceholder', 'Enter your fullname')}
                    required
                    disabled={status === 'sending'}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email">{t('contact.emailFormLabel', 'Email *')}</label>
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onInvalid={(e) => {
                      const target = e.target as HTMLInputElement;
                      if (target.validity.valueMissing) {
                        target.setCustomValidity(
                          t('validation.emailRequired', 'Email is required'),
                        );
                      } else if (target.validity.typeMismatch) {
                        target.setCustomValidity(
                          t('validation.emailInvalid', 'Please enter a valid email address'),
                        );
                      }
                    }}
                    onInput={(e) => (e.target as HTMLInputElement).setCustomValidity('')}
                    placeholder={t('contact.emailPlaceholder', 'Enter your email')}
                    required
                    disabled={status === 'sending'}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="message">{t('contact.messageLabel', 'Message *')}</label>
                  <textarea
                    id="message"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    onInvalid={(e) => {
                      const target = e.target as HTMLTextAreaElement;
                      if (target.validity.valueMissing) {
                        target.setCustomValidity(
                          t('validation.messageRequired', 'Message is required'),
                        );
                      }
                    }}
                    onInput={(e) => (e.target as HTMLTextAreaElement).setCustomValidity('')}
                    placeholder={t('contact.messagePlaceholder', 'Enter your message')}
                    rows={4}
                    required
                    disabled={status === 'sending'}
                  />
                </div>

                <div className="form-actions">
                  <button
                    type="submit"
                    className={`btn-send ${status === 'sending' || !isFormValid ? 'disabled' : ''}`}
                    disabled={status === 'sending' || !isFormValid}
                    style={{
                      opacity: status === 'sending' || !isFormValid ? 0.6 : 1,
                      cursor: status === 'sending' || !isFormValid ? 'not-allowed' : 'pointer',
                    }}
                  >
                    {status === 'sending' ? (
                      <span
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '8px',
                        }}
                      >
                        <svg
                          className="animate-spin"
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                        >
                          <title>Sending</title>
                          <path d="M21 12a9 9 0 1 1-6.219-8.56"></path>
                        </svg>
                        {t('contact.sending', 'Sending...')}
                      </span>
                    ) : (
                      t('contact.sendButton', 'Send message')
                    )}
                  </button>
                </div>
              </form>
            </div>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Contact;
